import React, { createContext, useContext, useMemo, useState } from 'react'
import { isEqual } from 'lodash'
import { FilterParam } from './FilterBar.types'
import { DropdownItemType } from './FilterDropdowns/FilterDropdown.constants'

// LabelValue interface for complex filter values
export interface LabelValue {
  label: string
  value: string
}

// FilterValue can be either a string (auto-converted) or LabelValue object
export type FilterValue = string | LabelValue

// Const object defining all valid filter value keys
const FILTER_VALUE_KEYS = {
  ORGANIZATION: 'organizations',
  OWNER: 'owners',
  QUESTIONNAIRE_STATUS: 'questionnaireStatuses',
  QUESTIONNAIRE_VENDOR: 'questionnaireVendors',
  QUESTIONNAIRE_ASSIGNEE: 'questionnaireAssignees',
  QUESTION_ANSWER_STATUS: 'questionAnswerStatuses',
  QUESTION_JUDGMENT: 'questionJudgments',
  QUESTION_CONTAINS: 'questionContains',
  QUESTION_VIEW: 'questionViews',
  AUDIT_RUN_OWNER: 'auditRunOwners',
  AUDIT_RUN_CONTROL: 'auditRunControls',
  AUDIT_RUN_STATUS: 'auditRunStatuses',
  AUDIT_RUN_RESULT: 'auditRunResults',
  COMPANY: 'companies',
  REPORT_TYPE: 'reportTypes',
  TRUST_SERVICE_CRITERIA: 'trustServiceCriteria',
  AUDITOR: 'auditors',
  USER_ROLE: 'userRoles',
  EMAIL_NOTIFICATION: 'emailNotifications',
  USER_DEPARTMENT: 'userDepartments',
  USER_STATUS: 'userStatuses',
  AUDIT_FRAMEWORKS: 'audit_frameworks',
  AUDIT_STATUS: 'audit_status',
  STATUS: 'status',
  DASHBOARD_FRAMEWORK: 'dashboardFramework',
  DASHBOARD_POLICY: 'dashboardPolicy',
  DASHBOARD_AUDIT: 'dashboardAudit',
  DASHBOARD_RISK: 'dashboardRisk',
  DASHBOARD_DEPARTMENT: 'dashboardDepartment',
  DASHBOARD_OWNER: 'dashboardOwner',
  DASHBOARD_DATE_RANGE: 'dashboardDateRange',
  REPORT_PLAYBOOK: 'reportPlaybook',
  ACCESS_ROLES: 'accessRoles',
  PLAYBOOK_CATEGORIES: 'playbookCategories',
  VISIBILITIES: 'visibilities',
  TAGS: 'tags',
  ACTIVITY_ACTORS: 'activityActors',
  ACTIVITY_OBJECT_TYPES: 'activityObjectTypes',
  ACCOUNT_TYPES: 'accountTypes',
  TRUSTERO_USERS: 'trusteroUsers',
  CITATION_POLICIES: 'citationPolicies',
  CITATION_CONTROLS: 'citationControls',
  CITATION_EVIDENCES: 'citationEvidences',
  CITATION_REQUESTS: 'citationRequests',
  CITATION_RISKS: 'citationRisks',
  CITATION_TYPES: 'citationTypes',
} as const

// Type for the filter value keys (union of the const values)
export type ValueKey =
  (typeof FILTER_VALUE_KEYS)[keyof typeof FILTER_VALUE_KEYS]

// Type for the values object - now all values are FilterValue arrays
export type FilterBarValues = {
  organizations: FilterValue[]
  owners: FilterValue[]
  questionnaireStatuses: FilterValue[]
  questionnaireVendors: FilterValue[]
  questionnaireAssignees: FilterValue[]
  questionAnswerStatuses: FilterValue[]
  questionJudgments: FilterValue[]
  questionContains: FilterValue[]
  questionViews: FilterValue[]
  auditRunOwners: FilterValue[]
  auditRunControls: FilterValue[]
  auditRunStatuses: FilterValue[]
  auditRunResults: FilterValue[]
  companies: FilterValue[]
  reportTypes: FilterValue[]
  trustServiceCriteria: FilterValue[]
  auditors: FilterValue[]
  userRoles: FilterValue[]
  emailNotifications: FilterValue[]
  userDepartments: FilterValue[]
  userStatuses: FilterValue[]
  audit_frameworks: FilterValue[]
  audit_status: FilterValue[]
  status: FilterValue[]
  dashboardFramework: FilterValue[]
  dashboardPolicy: FilterValue[]
  dashboardAudit: FilterValue[]
  dashboardRisk: FilterValue[]
  dashboardDepartment: FilterValue[]
  dashboardOwner: FilterValue[]
  dashboardDateRange: FilterValue[]
  reportPlaybook: FilterValue[]
  accessRoles: FilterValue[]
  playbookCategories: FilterValue[]
  visibilities: FilterValue[]
  tags: FilterValue[]
  activityActors: FilterValue[]
  activityObjectTypes: FilterValue[]
  accountTypes: FilterValue[]
  trusteroUsers: FilterValue[]
  citationPolicies: FilterValue[]
  citationControls: FilterValue[]
  citationEvidences: FilterValue[]
  citationRequests: FilterValue[]
  citationRisks: FilterValue[]
  citationTypes: FilterValue[]
}

// Type for transformation functions - now they work with FilterValue arrays
export type FilterTransformFunction = (
  value: FilterValue[],
) => DropdownItemType[]

// Type for the transformation registry - properly typed for each filter param
export type FilterTransformRegistry = {
  [K in FilterParam]?: FilterTransformFunction
}

export type FilterBarValuesState = {
  values: FilterBarValues
  setValues: <K extends keyof FilterBarValues>(
    key: K,
    value: FilterBarValues[K],
  ) => void
  // Add transformation functions
  transformFilter: (
    filterParam: FilterParam,
    valueKey: ValueKey,
  ) => DropdownItemType[]
  registerTransform: (
    filterParam: FilterParam,
    transformFn: FilterTransformFunction,
  ) => void
}

const initialFilterBarValuesState: FilterBarValuesState = {
  values: {
    organizations: [],
    owners: [],
    questionnaireStatuses: [],
    questionnaireVendors: [],
    questionnaireAssignees: [],
    questionAnswerStatuses: [],
    questionJudgments: [],
    questionContains: [],
    questionViews: [],
    auditRunOwners: [],
    auditRunControls: [],
    auditRunStatuses: [],
    auditRunResults: [],
    companies: [],
    reportTypes: [],
    trustServiceCriteria: [],
    auditors: [],
    userRoles: [],
    emailNotifications: [],
    userDepartments: [],
    userStatuses: [],
    audit_frameworks: [],
    audit_status: [],
    status: [],
    dashboardFramework: [],
    dashboardPolicy: [],
    dashboardAudit: [],
    dashboardRisk: [],
    dashboardDepartment: [],
    dashboardOwner: [],
    dashboardDateRange: [],
    reportPlaybook: [],
    accessRoles: [],
    playbookCategories: [],
    visibilities: [],
    tags: [],
    activityActors: [],
    activityObjectTypes: [],
    accountTypes: [],
    trusteroUsers: [],
    citationPolicies: [],
    citationControls: [],
    citationEvidences: [],
    citationRequests: [],
    citationRisks: [],
    citationTypes: [],
  },
  setValues: () => {
    // This will be overridden by the provider
  },
  transformFilter: () => {
    // This will be overridden by the provider
    return []
  },
  registerTransform: () => {
    // This will be overridden by the provider
  },
}

const FilterBarValuesContext = createContext<FilterBarValuesState>(
  initialFilterBarValuesState,
)

export const useFilterBarValues = (): FilterBarValuesState =>
  useContext(FilterBarValuesContext)

export const FilterBarValuesProvider = ({
  children,
}: React.PropsWithChildren): JSX.Element => {
  const [values, setValuesState] = useState<FilterBarValues>({
    organizations: [],
    owners: [],
    questionnaireStatuses: [],
    questionnaireVendors: [],
    questionnaireAssignees: [],
    questionAnswerStatuses: [],
    questionJudgments: [],
    questionContains: [],
    questionViews: [],
    auditRunOwners: [],
    auditRunControls: [],
    auditRunStatuses: [],
    auditRunResults: [],
    companies: [],
    reportTypes: [],
    trustServiceCriteria: [],
    auditors: [],
    userRoles: [],
    emailNotifications: [],
    userDepartments: [],
    userStatuses: [],
    audit_frameworks: [],
    audit_status: [],
    status: [],
    dashboardFramework: [],
    dashboardPolicy: [],
    dashboardAudit: [],
    dashboardRisk: [],
    dashboardDepartment: [],
    dashboardOwner: [],
    dashboardDateRange: [],
    reportPlaybook: [],
    accessRoles: [],
    playbookCategories: [],
    visibilities: [],
    tags: [],
    activityActors: [],
    activityObjectTypes: [],
    accountTypes: [],
    trusteroUsers: [],
    citationPolicies: [],
    citationControls: [],
    citationEvidences: [],
    citationRequests: [],
    citationRisks: [],
    citationTypes: [],
  })
  const [transformRegistry, setTransformRegistry] =
    useState<FilterTransformRegistry>({})

  const transformFilter = useMemo(() => {
    return (
      filterParam: FilterParam,
      valueKey: ValueKey,
    ): DropdownItemType[] => {
      const currentValues = values[valueKey as keyof FilterBarValues]
      const transformFn = transformRegistry[filterParam]

      if (transformFn) {
        return transformFn(currentValues)
      }

      // Default transformation - automatically handles both string and LabelValue
      if (Array.isArray(currentValues)) {
        return currentValues.map((item) => {
          if (typeof item === 'string') {
            return { value: item, label: item } // Auto-convert string to label/value
          } else {
            return { value: item.value, label: item.label } // Extract from LabelValue
          }
        })
      }

      return []
    }
  }, [transformRegistry, values])

  const registerTransform = useMemo(() => {
    return (filterParam: FilterParam, transformFn: FilterTransformFunction) => {
      setTransformRegistry((prev) => ({
        ...prev,
        [filterParam]: transformFn,
      }))
    }
  }, [])

  const setValues = useMemo(() => {
    return <K extends keyof FilterBarValues>(
      key: K,
      value: FilterBarValues[K],
    ) => {
      const currentValue = values[key]

      // Deep comparison to check if values have actually changed
      if (isEqual(currentValue, value)) {
        return // No change, don't update state
      }

      // Values have changed, update state
      setValuesState((prev) => ({
        ...prev,
        [key]: value,
      }))
    }
  }, [values])

  const value = useMemo(
    () => ({
      values,
      setValues,
      transformFilter,
      registerTransform,
    }),
    [values, setValues, transformFilter, registerTransform],
  )

  return (
    <FilterBarValuesContext.Provider value={value}>
      {children}
    </FilterBarValuesContext.Provider>
  )
}
